import { useMemo, useState } from 'react';

import { useIsFlexSwitchToWhatsappCity } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-is-flex-switch-to-whatsapp-city.hook';
import { SchedulesSearchProcedure, ScheduleType, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { useExpandedSlotsGrid } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-expanded-slots-grid.hook';
import { ScheduleTabAnalytics } from '@/app/(main)/(pages)/busca/[slug]/_utils/schedule-tab-analytics.util';
import { useUserLocation } from '@/hooks/use-user-location.hook';
import { AnalyticsEnum, AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

export enum TabsType {
  SCHEDULED = 'SCHEDULED',
  ARRIVAL_ORDER = 'ARRIVAL_ORDER',
  FLEX = 'FLEX',
  WHATSAPP = 'WHATSAPP',
  STANDALONE = 'STANDALONE',
  LABORATORIAL = 'LABORATORIAL',
}

type TabItem = { title: string; key: TabsType };

export const TABS_LABELS: Record<TabsType, TabItem> = {
  [TabsType.SCHEDULED]: { title: 'Hora marcada', key: TabsType.SCHEDULED },
  [TabsType.ARRIVAL_ORDER]: { title: 'Ordem de chegada', key: TabsType.ARRIVAL_ORDER },
  [TabsType.FLEX]: { title: 'Encaixe', key: TabsType.FLEX },
  [TabsType.WHATSAPP]: { title: 'WhatsApp', key: TabsType.WHATSAPP },
  [TabsType.STANDALONE]: { title: 'Guia avulsa', key: TabsType.STANDALONE },
  [TabsType.LABORATORIAL]: { title: 'Ordem de chegada', key: TabsType.LABORATORIAL },
} as const;

export const TAB_ANALYTICS_NAMES: Record<TabsType, string> = {
  [TabsType.SCHEDULED]: 'Hora marcada',
  [TabsType.ARRIVAL_ORDER]: 'Ordem de chegada',
  [TabsType.FLEX]: 'Flex',
  [TabsType.WHATSAPP]: 'WhatsApp',
  [TabsType.STANDALONE]: 'Guia avulsa',
  [TabsType.LABORATORIAL]: 'Ordem de chegada',
};

export const TAB_ANALYTICS_EVENTS: Record<TabsType, AnalyticsEnumKeyType> = {
  [TabsType.SCHEDULED]: AnalyticsEnum.CLICK_SEARCH_RESULT_TAB_SCHEDULED,
  [TabsType.ARRIVAL_ORDER]: AnalyticsEnum.CLICK_SEARCH_RESULT_TAB_ARRIVAL_ORDER,
  [TabsType.LABORATORIAL]: AnalyticsEnum.CLICK_SEARCH_RESULT_TAB_ARRIVAL_ORDER,
  [TabsType.FLEX]: AnalyticsEnum.CLICK_SEARCH_RESULT_TAB_FLEX,
  [TabsType.WHATSAPP]: AnalyticsEnum.CLICK_SEARCH_RESULT_TAB_WHATSAPP,
  [TabsType.STANDALONE]: AnalyticsEnum.CLICK_SEARCH_RESULT_TAB_STANDALONE,
};

const TAB_SCHEDULE_TYPE: Partial<Record<TabsType, ScheduleType>> = {
  [TabsType.SCHEDULED]: ScheduleType.SCHEDULED,
  [TabsType.ARRIVAL_ORDER]: ScheduleType.ARRIVAL_ORDER,
  [TabsType.LABORATORIAL]: ScheduleType.LABORATORIAL,
  [TabsType.FLEX]: ScheduleType.FLEX,
  [TabsType.WHATSAPP]: ScheduleType.FLEX,
};

function countTabSchedules(tab: TabsType, schedule?: SearchPartnerData): number {
  const scheduleType = TAB_SCHEDULE_TYPE[tab];

  return scheduleType ? schedule?.schedulesTypes?.[scheduleType]?.length ?? 0 : 0;
}

const hasType = (schedule?: SearchPartnerData, type?: ScheduleType) =>
  Boolean(type && schedule?.schedulesTypes?.[type]?.length);

const isTelehealthProcedure = (procedures?: SchedulesSearchProcedure[]) =>
  procedures?.some((procedure) => procedure.availableForTelehealth) ?? false;

const hasStandalone = (schedule?: SearchPartnerData) =>
  Boolean(schedule?.partner.hasStandalonePurchase) && !isTelehealthProcedure(schedule?.procedures);

export function useScheduleTab(schedule: SearchPartnerData | undefined, tabAnalytics: ScheduleTabAnalytics) {
  const [selectedTab, setSelectedTab] = useState<TabsType | undefined>();
  const isWhatsappCity = useIsFlexSwitchToWhatsappCity(schedule?.partner.facility.IBGECode);
  const { userLocation } = useUserLocation();
  const { variant } = useExpandedSlotsGrid();

  const tabs = useMemo(() => {
    if (!schedule?.schedulesTypes) {
      return [];
    }

    const hasScheduled = hasType(schedule, ScheduleType.SCHEDULED);
    const hasArrivalOrder = hasType(schedule, ScheduleType.ARRIVAL_ORDER);
    const hasLaboratorial = hasType(schedule, ScheduleType.LABORATORIAL);
    const hasStandaloneTab = hasStandalone(schedule);
    const hasFlex = hasType(schedule, ScheduleType.FLEX);
    const hasScheduledAndArrivalOrder = hasScheduled && hasArrivalOrder;
    const flexTabLabel = isWhatsappCity ? TABS_LABELS[TabsType.WHATSAPP] : TABS_LABELS[TabsType.FLEX];

    const tabsList: TabItem[] = [];

    if (hasScheduled) {
      tabsList.push(TABS_LABELS[TabsType.SCHEDULED]);
    }
    if (hasArrivalOrder) {
      tabsList.push(TABS_LABELS[TabsType.ARRIVAL_ORDER]);
    }
    if (hasLaboratorial) {
      tabsList.push(TABS_LABELS[TabsType.LABORATORIAL]);
    }

    if (hasFlex && hasStandaloneTab && hasScheduledAndArrivalOrder) {
      tabsList.push(flexTabLabel);
      tabsList.push(TABS_LABELS[TabsType.STANDALONE]);

      return tabsList;
    }

    if (hasFlex && !hasStandaloneTab) {
      tabsList.push(flexTabLabel);
    }

    if (!hasFlex && hasStandaloneTab) {
      tabsList.push(TABS_LABELS[TabsType.STANDALONE]);
    }

    if (hasFlex && hasStandaloneTab && !hasScheduledAndArrivalOrder) {
      tabsList.push(flexTabLabel);
      tabsList.push(TABS_LABELS[TabsType.STANDALONE]);
    }

    return tabsList;
  }, [schedule, isWhatsappCity]);

  function onValueChange(value: string) {
    const tab = value as TabsType;
    const event = TAB_ANALYTICS_EVENTS[tab];
    const previousTab = selectedTab ?? tabs[0]?.key;

    if (event) {
      getAnalyticsPlugin().track(event, {
        aba_anterior: previousTab ? TAB_ANALYTICS_NAMES[previousTab] : undefined,
        qtd_resultados_aba: countTabSchedules(tab, schedule),
        procedure_codes: schedule?.procedures.map((procedure) => procedure.code),
        city: userLocation?.city,
        modalidade: tabAnalytics.modalidade,
        variante: variant,
        origem: tabAnalytics.origin,
      });
    }

    setSelectedTab(tab);
  }

  return { tabs, selectedTab, onValueChange };
}
